import Dialog, { useDialog, useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import MultiSelectField from "@imminently/decisively-ui/MultiSelectField";
import SelectField from "@imminently/decisively-ui/SelectField";
import TextField from "@imminently/decisively-ui/TextField";
import Form from "@imminently/decisively-ui/Form";
import { z } from "zod";
import { useScope } from "../redux/scope";
import { LOCALE_OPTIONS } from "./ProjectModal_localeOptions";
import { Project } from "@packages/commons";
import { projectService } from "@/app/providers/services";
import { useTranslation } from "react-i18next";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import { crudCreate, crudUpdate, useAsyncDispatch } from "@/legacy/redux";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { useCallback } from "react";
import SwitchField from "@imminently/decisively-ui/SwitchField";

const capitaliseFirstLetter = (string: string): string => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const ProjectSchema = z.object({
  name: z.string().trim().min(1, "Required"),
  description: z.string().optional(),
  tags: z.array(z.string()).default([]),
  visibility: z.enum(["public", "private"]).default("public"),
  locale: z.string().default("en-au"),
  locked: z.boolean().default(false),
  disableGuids: z.boolean().default(false),
  workspace: z.string(),
});

/** Props for the ProjectModal dialog component. */
export interface ProjectModalProps {
  project?: Project;
}

/** Dialog body for creating or editing a project. */
export const ProjectModal = ({ project }: ProjectModalProps) => {
  const { workspace } = useScope();
  const dialogContext = useDialogContext();
  const { t } = useTranslation();
  const dispatchAsync = useAsyncDispatch();

  const isCreate = project === undefined;

  const defaultValues = {
    name: project?.name ?? "",
    description: project?.description ?? "",
    tags: project?.tags ?? [],
    visibility: (project?.visibility as "public" | "private" | undefined) ?? "public",
    locale: project?.locale ?? "en-au",
    locked: project?.locked ?? false,
    disableGuids: project?.disableGuids ?? false,
    workspace,
  };

  return (
    <Dialog.Content className="flex flex-col">
      <DialogHeader title={project ? t("projects.edit_project") : t("projects.create_label")} />

      <Form
        defaultValues={defaultValues}
        schema={ProjectSchema}
        onSubmit={async (submitValues) => {
          const payload = {
            name: submitValues.name,
            description: submitValues.description,
            tags: submitValues.tags,
            visibility: submitValues.visibility,
            locale: submitValues.locale,
            locked: submitValues.locked,
            workspace: submitValues.workspace,
          };
          if (project?.id) {
            // disableGuids is create-only: it doesn't retro-convert ids already minted
            // on existing releases, so it's not part of the edit payload.
            await dispatchAsync(crudUpdate("models", project.id, payload, { refresh: true }));
          } else {
            await dispatchAsync(
              crudCreate("models", { ...payload, disableGuids: submitValues.disableGuids }, { refresh: true }),
            );
          }
          projectService.invalidateAllQueries();
          dialogContext?.close();
        }}
        className="overflow-y-auto"
      >
        {() => (
          <>
            <Dialog.Body asChild={true}>
              <FormPanel>
                <TextField
                  required
                  className="w-full"
                  name="name"
                  label={t("projects.project_name")}
                  fullWidth
                />

                <TextField
                  className="w-full"
                  name="description"
                  label={t("description")}
                  fullWidth
                />

                <MultiSelectField
                  name="tags"
                  label={t("tags")}
                  options={[]}
                  allowArbitrary
                  fullWidth
                />

                <SelectField
                  name="visibility"
                  label={t("projects.visibility_label")}
                  options={[
                    {
                      label: capitaliseFirstLetter(t("projects.visibility_public")),
                      value: "public",
                    },
                    {
                      label: capitaliseFirstLetter(t("projects.visibility_private")),
                      value: "private",
                    },
                  ]}
                  fullWidth
                />

                <SelectField
                  name="locale"
                  label={t("projects.locale_label")}
                  options={LOCALE_OPTIONS.map((it) => ({ value: it.value, label: it.key }))}
                  fullWidth
                />

                <SwitchField
                  name="locked"
                  label={t("projects.lock_project")}
                  helperText={t("projects.lock_project_description")}
                />

                <SwitchField
                  name="disableGuids"
                  label={t("projects.disable_guids")}
                  helperText={t("projects.disable_guids_description")}
                  disabled={!isCreate}
                />
              </FormPanel>
            </Dialog.Body>
            <Dialog.Actions>
              <Dialog.Actions.PrimaryButton
                label={isCreate ? t("create") : t("update")}
                type="submit"
              />
            </Dialog.Actions>
          </>
        )}
      </Form>
    </Dialog.Content>
  );
};

/** Returns a callback that opens the create-project dialog. */
export const useAddProjectModal = () => {
  const { pushDialog } = useDialog();

  return useCallback(() => {
    pushDialog({
      layer: WORKSPACE_LAYER,
      options: { intent: "form" },
      component: ProjectModal,
    });
  }, [pushDialog]);
};

/** Returns a callback that opens the edit-project dialog for a given project. */
export const useEditProject = () => {
  const { pushDialog } = useDialog();

  return useCallback(
    (project?: Project) => {
      pushDialog({
        layer: WORKSPACE_LAYER,
        options: { intent: "form" },
        component: (props) => (
          <ProjectModal
            {...props}
            project={project}
          />
        ),
      });
    },
    [pushDialog],
  );
};
